/**
 * Gravação de voz no navegador para os testes do assistente. O áudio é convertido para WAV
 * (16 kHz, mono) — formato que todos os serviços de transcrição aceitam — e enviado em base64.
 */

export interface RecordedAudio {
  base64: string;
  mimeType: 'audio/wav';
  durationMs: number;
}

export interface VoiceRecorder {
  stop(): Promise<RecordedAudio>;
  cancel(): void;
}

const MAX_MS = 120_000;

export function voiceSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

export async function startRecording(onLimit?: () => void): Promise<VoiceRecorder> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
  const recorder = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  const started = Date.now();
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  recorder.start();
  const limit = setTimeout(() => onLimit?.(), MAX_MS);
  const release = () => {
    clearTimeout(limit);
    stream.getTracks().forEach((t) => t.stop());
  };
  return {
    cancel() {
      if (recorder.state !== 'inactive') recorder.stop();
      release();
    },
    stop() {
      return new Promise<RecordedAudio>((resolve, reject) => {
        recorder.onstop = async () => {
          release();
          try {
            const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
            const wav = await toWav16kMono(await blob.arrayBuffer());
            resolve({ base64: bytesToBase64(wav), mimeType: 'audio/wav', durationMs: Date.now() - started });
          } catch (e) {
            reject(e instanceof Error ? e : new Error('Não foi possível converter o áudio'));
          }
        };
        if (recorder.state !== 'inactive') recorder.stop();
      });
    },
  };
}

async function toWav16kMono(data: ArrayBuffer): Promise<Uint8Array> {
  const ctx = new AudioContext();
  const decoded = await ctx.decodeAudioData(data);
  await ctx.close();
  const rate = 16_000;
  const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(decoded.duration * rate)), rate);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const pcm = (await offline.startRendering()).getChannelData(0);

  const out = new DataView(new ArrayBuffer(44 + pcm.length * 2));
  const str = (o: number, s: string) => [...s].forEach((c, i) => out.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  out.setUint32(4, 36 + pcm.length * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  out.setUint32(16, 16, true);
  out.setUint16(20, 1, true);
  out.setUint16(22, 1, true);
  out.setUint32(24, rate, true);
  out.setUint32(28, rate * 2, true);
  out.setUint16(32, 2, true);
  out.setUint16(34, 16, true);
  str(36, 'data');
  out.setUint32(40, pcm.length * 2, true);
  for (let i = 0; i < pcm.length; i++) {
    const v = Math.max(-1, Math.min(1, pcm[i]!));
    out.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Uint8Array(out.buffer);
}

function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
